import { toast } from 'react-toastify';
import store from 'redux/store';
import { waitDialog } from 'dialogs';
import type { CreateTarget } from 'actions';
import { getEvent, type FetchedEvent } from 'api/events/endpoints/getEvent';
import { createEvent, updateEvent, type Event } from '../../../../../redux/slices/eventsSlice';
import EventFileEvent from '../event_fileEvent';

export type ShowEditEventArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

// Persist via create/update and close on success; reject (surfacing the backend
// message) on failure so the modal can show it and stay open.
const saveEvent = async (eventData: Event, close: () => void): Promise<{ success: true }> => {
  const result: any = await store.dispatch((eventData.event_id ? updateEvent(eventData) : createEvent(eventData)) as any);
  if (updateEvent.fulfilled.match(result) || createEvent.fulfilled.match(result)) {
    close();
    return { success: true };
  }
  const payload = result?.payload;
  const errorMessage = payload?.error || payload?.message || result?.error?.message || 'Failed to save event.';
  return Promise.reject(new Error(errorMessage));
};

/**
 * Open the event create / edit dialog and resolve when it closes (so the caller
 * can refresh). Standalone async: for edit it always fetches the record by id
 * (boundary-validated), so it works from the Workspace — which never populates
 * the list slice — and never opens the modal on a loosely-cast store row.
 */
export const showEditEvent = async (args: ShowEditEventArgs): Promise<void> => {
  const createIn = args.mode === 'create' ? args.createIn : undefined;
  let event: FetchedEvent | null = null;
  if (args.mode === 'edit') {
    event = await getEvent(args.id);
    if (!event) {
      toast.error('Event not found; the list may be out of date. Please refresh.');
      return;
    }
  }
  await waitDialog<void>((ok) => <EventFileEvent show onClose={ok} event={event} onSave={(data) => saveEvent(data, ok)} createIn={createIn} />);
};
